import * as THREE from 'three';

export const WAVE_SCENE_ID = 'wave';

export const sceneMeta = {
  id: WAVE_SCENE_ID,
  label: 'Wave lattice',
  defaultBindings: {},
  /** Slider ranges / labels for Performance → Tune */
  paramSpecs: {
    zoom: { min: 1.5, max: 24, label: 'Zoom' },
    lineCount: { min: 4, max: 96, step: 1, label: 'Line count' },
    color: { type: 'hue', label: 'Color' },
  },
  defaultParams: {
    zoom: 5,
    lineCount: 24,
    color: '#66aaff',
  },
  bindingTargets: [
    { key: 'twist', label: 'Twist amount', min: 0, max: 1 },
    { key: 'spread', label: 'Spread', min: 0, max: 1 },
    { key: 'brightness', label: 'Brightness', min: 0, max: 1 },
    { key: 'pulse', label: 'BPM pulse', min: 0, max: 1 },
  ],
};

function parseColor(hex) {
  const s = String(hex || '#66aaff').replace('#', '');
  const n = parseInt(s.length === 3 ? s.split('').map((c) => c + c).join('') : s, 16);
  return Number.isFinite(n) ? n : 0x66aaff;
}

/**
 * Rotating line lattice; second registered scene to validate dispose/switch.
 */
export class WaveScene {
  constructor() {
    this.threeScene = new THREE.Scene();
    this._lines = [];
    this._root = new THREE.Group();
    this.threeScene.add(this._root);
    this._params = { ...sceneMeta.defaultParams };
    this._camera = null;
    this._inited = false;
  }

  getMidiTargets() {
    return sceneMeta.bindingTargets;
  }

  getSceneLabel() {
    return sceneMeta.label;
  }

  _syncCameraZoom() {
    if (!this._camera) return;
    const z = Number(this._params.zoom);
    if (!Number.isFinite(z)) return;
    this._camera.position.z = z;
  }

  applyParams(params = {}) {
    this._params = { ...sceneMeta.defaultParams, ...params };
    this._syncCameraZoom();
    this._rebuildGeometry();
  }

  init(ctx) {
    if (this._inited) return;
    this._camera = typeof ctx?.getCamera === 'function' ? ctx.getCamera() : null;
    this._syncCameraZoom();
    this._rebuildGeometry();
    this._inited = true;
  }

  _rebuildGeometry() {
    for (const l of this._lines) {
      l.geometry.dispose();
      l.material.dispose();
    }
    this._lines = [];
    this._root.clear();

    const n = Math.max(4, Math.min(64, Math.round(Number(this._params.lineCount) || 24)));
    const mat = new THREE.LineBasicMaterial({
      color: parseColor(this._params.color),
      transparent: true,
      opacity: 0.45,
    });

    for (let i = 0; i < n; i++) {
      const t = (i / n) * Math.PI * 2;
      const pts = [];
      for (let k = 0; k <= 64; k++) {
        const u = (k / 64) * Math.PI * 2;
        const r = 1.2 + Math.sin(u * 3 + t) * 0.15;
        pts.push(new THREE.Vector3(Math.cos(u) * r, Math.sin(u) * r, Math.sin(u * 2 + t) * 0.3));
      }
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      const line = new THREE.Line(geo, mat.clone());
      line.rotation.z = t;
      this._root.add(line);
      this._lines.push(line);
    }
  }

  update(dt, resolved = {}) {
    const twist = resolved.twist ?? 0.5;
    const spread = resolved.spread ?? 0.3;
    const br = resolved.brightness ?? 0.4;
    const pulse = resolved.pulse ?? 0;

    this._root.rotation.z += dt * (0.15 + twist * 0.5);
    this._root.rotation.x = spread * 0.4;
    this._root.scale.setScalar(1 + pulse * 0.08 + br * 0.15);

    let i = 0;
    for (const line of this._lines) {
      line.material.opacity = 0.15 + br * 0.55 + pulse * 0.2;
      line.rotation.z += dt * (0.02 + i * 0.002) * (0.5 + twist);
      i++;
    }
  }

  resize(_w, _h) {}

  dispose() {
    for (const l of this._lines) {
      l.geometry.dispose();
      l.material.dispose();
    }
    this._lines = [];
    this._root.clear();
    this._camera = null;
    this._inited = false;
  }
}
